import { createColumnHelper } from '@tanstack/react-table';
import { Edit } from 'lucide-react';

import { truncate } from '@/portainer/filters/filters';

import { Badge } from '@@/Badge';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { Link } from '@@/Link';
import { Button } from '@@/buttons';

import { AlertRuleRow } from '../../types';
import { AlertSeverityBadge } from '../../AlertSeverityBadge';
import { formatThreshold } from '../../utils';
import {
  getAlertRuleCategoryLabel,
  resolveAlertRuleCategory,
} from '../alertRuleCategory';

export const columnHelper = createColumnHelper<AlertRuleRow>();

// Reads metrics only the vulnerability scanning policy produces.
const IMAGE_VULNERABILITIES_TEMPLATE =
  'builtin.edge.kubernetes.image-vulnerabilities';
const EDGE_ALERT_TEMPLATE_LABEL = 'portainer.io/edge-alert-template';

export function createColumns(
  onToggle: (alertRule: AlertRuleRow, enabled: boolean) => void,
  togglingId?: number,
  isAlertManagerEnabled?: boolean,
  hasVulnerabilityScanningPolicy?: boolean
) {
  return [
    columnHelper.accessor('name', {
      header: 'Alert Rule',
      meta: { width: '42%' },
      cell: ({ getValue, row }) => {
        const {
          isInternal,
          supportedAgentVersion,
          supportedEnvironmentTypes,
          useTiers,
          category,
        } = row.original;

        const resolvedCategory = resolveAlertRuleCategory({
          category,
          labels: row.original.labels,
          supportedEnvironmentTypes,
        });

        return (
          <div className="flex flex-col gap-1">
            <span className="font-medium whitespace-nowrap">{getValue()}</span>
            <div className="flex flex-nowrap gap-1 overflow-hidden">
              <Badge type="info" className="text-xs">
                {getAlertRuleCategoryLabel(resolvedCategory)}
              </Badge>
              {isInternal && (
                <Badge type="info" className="text-xs">
                  System
                </Badge>
              )}
              {useTiers && (
                <Badge type="muted" className="text-xs">
                  Multi-tier
                </Badge>
              )}
              {supportedEnvironmentTypes && (
                <Badge type="success" className="text-xs">
                  {supportedEnvironmentTypes}
                </Badge>
              )}
              {supportedAgentVersion && (
                <Badge type="warn" className="text-xs">
                  {supportedAgentVersion}
                </Badge>
              )}
              {isMissingScanner(
                row.original,
                hasVulnerabilityScanningPolicy
              ) && (
                <TooltipWithChildren
                  message="No vulnerability scanning policy is attached, so this rule has no findings to measure and will not fire. Attach the policy to an environment group to resume alerting."
                  position="top"
                >
                  <span>
                    <Badge type="warn" className="text-xs">
                      Inactive - no scanner
                    </Badge>
                  </span>
                </TooltipWithChildren>
              )}
            </div>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      meta: { width: '8%' },
      cell: ({ row }) => {
        const alertRule = row.original;
        const message = disabledReason(
          alertRule,
          isAlertManagerEnabled,
          hasVulnerabilityScanningPolicy
        );
        const isDisabled = !!message;

        const switchComponent = (
          <Switch
            id={`toggle-alert-rule-${alertRule.id}`}
            name={`toggle-alert-rule-${alertRule.id}`}
            checked={alertRule.enabled}
            onChange={() => onToggle(alertRule, !alertRule.enabled)}
            disabled={togglingId === alertRule.id || isDisabled}
            data-cy={`toggle-alert-rule-${alertRule.id}`}
          />
        );

        return (
          <div className="flex items-center gap-2">
            {isDisabled ? (
              <TooltipWithChildren message={message} position="top">
                <div className="inline-block cursor-not-allowed">
                  {switchComponent}
                </div>
              </TooltipWithChildren>
            ) : (
              switchComponent
            )}
            {isDisabled ? (
              <TooltipWithChildren message={message} position="top">
                <div className="inline-block cursor-not-allowed">
                  <Button
                    color="primary"
                    size="xsmall"
                    icon={Edit}
                    disabled
                    data-cy={`edit-alert-rule-${alertRule.id}-button`}
                  >
                    Edit
                  </Button>
                </div>
              </TooltipWithChildren>
            ) : (
              <>
                {!alertRule.isEditable ? (
                  <Button
                    color="primary"
                    size="xsmall"
                    icon={Edit}
                    disabled
                    data-cy={`edit-alert-rule-${alertRule.id}-button`}
                  >
                    Edit
                  </Button>
                ) : (
                  <Link
                    to="portainer.alerting.rule"
                    params={{
                      id: alertRule.id,
                    }}
                    data-cy={`edit-alert-rule-${alertRule.id}`}
                  >
                    <Button
                      color="primary"
                      size="xsmall"
                      icon={Edit}
                      data-cy={`edit-alert-rule-${alertRule.id}-button`}
                    >
                      Edit
                    </Button>
                  </Link>
                )}
              </>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      meta: { width: '15%' },
      cell: ({ getValue }) => {
        const description = getValue();
        if (!description) return '-';
        if (description.length <= 60) return description;
        return (
          <TooltipWithChildren message={description} position="top">
            <span>{truncate(description, 60)}</span>
          </TooltipWithChildren>
        );
      },
    }),
    columnHelper.accessor('severity', {
      header: 'Severity',
      meta: { width: '6%' },
      cell: ({ getValue }) => <AlertSeverityBadge severity={getValue()} />,
    }),
    columnHelper.accessor('threshold', {
      header: 'Threshold',
      meta: { width: '7%' },
      cell: ({ getValue, row }) => {
        const { useTiers, tiers, metricType } = row.original;

        if (useTiers && tiers?.length) {
          const activeTiers = tiers.filter((t) => t.enabled);
          if (activeTiers.length === 0) return '-';
          return activeTiers
            .map(
              (t) => `${t.severity} ${formatThreshold(t.threshold, metricType)}`
            )
            .join(' / ');
        }

        const threshold = getValue();
        if (threshold === undefined || threshold === null) {
          return '-';
        }
        return formatThreshold(threshold, metricType);
      },
    }),
    columnHelper.accessor('conditionOperator', {
      header: 'Condition',
      meta: { width: '6%' },
      cell: ({ getValue, row }) => {
        const operator = getValue();
        const { threshold } = row.original;

        if (threshold === undefined || threshold === null) {
          return '-';
        }
        return operator || '>';
      },
    }),
    columnHelper.accessor('duration', {
      header: 'Duration (min)',
      meta: { width: '7%' },
      cell: ({ getValue }) => {
        const duration = getValue();
        return duration ? `${duration}m` : '-';
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created At',
      meta: { width: '9%' },
    }),
  ];
}

// An enabled rule is always left toggleable so it can be switched off.
function disabledReason(
  alertRule: AlertRuleRow,
  isAlertManagerEnabled?: boolean,
  hasVulnerabilityScanningPolicy?: boolean
): string | undefined {
  if (alertRule.enabled) {
    return undefined;
  }

  if (!isAlertManagerEnabled) {
    return 'Enable the internal alert manager in Alert Settings to activate alert rules';
  }

  // undefined means still loading; disabling on that flickers the toggle.
  if (
    requiresVulnerabilityScanning(alertRule) &&
    hasVulnerabilityScanningPolicy === false
  ) {
    return 'Attach a vulnerability scanning policy to an environment group first — without a scanner this rule has no findings to measure';
  }

  return undefined;
}

// Left enabled on purpose so re-attaching the policy resumes alerting without
// reconfiguring thresholds; the dead state is surfaced instead.
function isMissingScanner(
  alertRule: AlertRuleRow,
  hasVulnerabilityScanningPolicy?: boolean
): boolean {
  return (
    alertRule.enabled &&
    requiresVulnerabilityScanning(alertRule) &&
    hasVulnerabilityScanningPolicy === false
  );
}

function requiresVulnerabilityScanning(alertRule: AlertRuleRow): boolean {
  return (
    alertRule.labels?.[EDGE_ALERT_TEMPLATE_LABEL] ===
    IMAGE_VULNERABILITIES_TEMPLATE
  );
}
